<!--
  PanelModal:复杂活动弹层容器
  - H5:底部 panel / drawer;PC:居中 modal
  - 提供遮罩、@fegobull/webapi useTeleport、背景滚动锁定
  - H5 顶部短横条仅作为视觉提示,不绑定拖拽手势
  - 右上角角标通过 badge slot 或 badgeSrc 注入,素材由业务侧提供
  - 不渲染 iPhone HomeIndicator 底部黑条
-->
<template>
  <div
    v-if="props.visible"
    class="panel-modal-mask"
    :class="[`page-${props.pageType}`]"
    :style="{ zIndex }"
    @click.self="onMaskClick"
  >
    <div
      class="panel-modal-stack"
      :class="[`page-${props.pageType}`]"
    >
      <section
        class="panel-modal-panel"
        :class="[
          `page-${props.pageType}`,
          { 'panel-unpadded': !props.panelPadding }
        ]"
        :style="panelStyle"
        role="dialog"
        aria-modal="true"
        :aria-label="props.title || undefined"
      >
        <div
          v-if="$slots.badge || props.badgeSrc"
          class="panel-modal-badge"
          aria-hidden="true"
        >
          <slot name="badge">
            <img
              v-if="props.badgeSrc"
              class="panel-modal-badge-image"
              :src="props.badgeSrc"
              :alt="props.badgeAlt"
            />
          </slot>
        </div>

        <header
          v-if="showHeaderIndicator || props.title || $slots.header"
          class="panel-modal-header"
        >
          <div
            v-if="showHeaderIndicator"
            class="panel-modal-indicator"
            aria-hidden="true"
          />
          <slot name="header">
            <h2
              v-if="props.title"
              class="panel-modal-title"
            >
              {{ props.title }}
            </h2>
          </slot>
        </header>

        <main
          class="panel-modal-body"
          :class="{ 'content-unpadded': !props.contentPadded }"
        >
          <slot />
        </main>

        <footer
          v-if="$slots.footer"
          class="panel-modal-footer"
          :class="{ 'content-unpadded': !props.contentPadded }"
        >
          <slot name="footer" />
        </footer>

        <div
          v-if="showHomeIndicator"
          class="panel-modal-home-indicator"
          aria-hidden="true"
        />
      </section>

      <button
        v-if="props.showCloseButton"
        type="button"
        class="panel-modal-close-button"
        :aria-label="props.closeButtonLabel"
        @click="$emit('close')"
      >
        <span class="panel-modal-close-line line-left" />
        <span class="panel-modal-close-line line-right" />
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import { computed } from 'vue-demi';
import type { ComputedRef } from 'vue-demi';
type WebapiUse = {
  useTeleport: (target: HTMLElement) => void;
  useLockScreen: (locked: ComputedRef<boolean>) => void;
  useZIndex: () => number;
};

// Vue2 FEGO 运行时惯例:require(...).use 避免 ESM interop 差异

type PageType = 'PC' | 'H5';
type Props = {
  visible: boolean;
  pageType: PageType;
  title?: string;
  showIndicator?: boolean;
  contentPadded?: boolean;
  closeOnMask?: boolean;
  showCloseButton?: boolean;
  closeButtonLabel?: string;
  badgeSrc?: string;
  badgeAlt?: string;
  panelBackground?: string;
  showHomeIndicator?: boolean;
  panelWidth?: string;
  panelPadding?: boolean;
};
const __sfc_main = {};
__sfc_main.props = {
  visible: {
    key: "visible",
    required: true,
    type: Boolean
  },
  pageType: {
    key: "pageType",
    required: true,
    type: null
  },
  title: {
    key: "title",
    required: false,
    type: String,
    default: ''
  },
  showIndicator: {
    key: "showIndicator",
    required: false,
    type: Boolean,
    default: true
  },
  contentPadded: {
    key: "contentPadded",
    required: false,
    type: Boolean,
    default: true
  },
  closeOnMask: {
    key: "closeOnMask",
    required: false,
    type: Boolean,
    default: true
  },
  showCloseButton: {
    key: "showCloseButton",
    required: false,
    type: Boolean,
    default: false
  },
  closeButtonLabel: {
    key: "closeButtonLabel",
    required: false,
    type: String,
    default: 'Close'
  },
  badgeSrc: {
    key: "badgeSrc",
    required: false,
    type: String,
    default: ''
  },
  badgeAlt: {
    key: "badgeAlt",
    required: false,
    type: String,
    default: ''
  },
  panelBackground: {
    key: "panelBackground",
    required: false,
    type: String,
    default: '#ffffff'
  },
  showHomeIndicator: {
    key: "showHomeIndicator",
    required: false,
    type: Boolean,
    default: false
  },
  panelWidth: {
    key: "panelWidth",
    required: false,
    type: String,
    default: ''
  },
  panelPadding: {
    key: "panelPadding",
    required: false,
    type: Boolean,
    default: true
  }
};
__sfc_main.setup = (__props, __ctx) => {
  const {
    useTeleport,
    useLockScreen,
    useZIndex
  } = (require('@fegobull/webapi') as {
    use: WebapiUse;
  }).use;
  const props = __props;
  const emit = __ctx.emit;

  // 弹层挂到 body 下,避免宿主容器 overflow/transform 裁切
  useTeleport(document.body);
  const zIndex = useZIndex();

  // visible 为真时锁定背景滚动,关闭后自动恢复
  useLockScreen(computed(() => props.visible));
  const showHeaderIndicator = computed<boolean>(() => props.pageType === 'H5' && props.showIndicator);
  const showHomeIndicator = computed<boolean>(() => props.pageType === 'H5' && props.showHomeIndicator);
  const panelStyle = computed<Record<string, string>>(() => {
    const style: Record<string, string> = {
      '--panel-modal-background': props.panelBackground
    };
    if (props.panelWidth) {
      style['--panel-modal-width'] = props.panelWidth;
    }
    return style;
  });
  function onMaskClick(): void {
    if (props.closeOnMask) {
      emit('close');
    }
  }
  return {
    props,
    zIndex,
    showHeaderIndicator,
    showHomeIndicator,
    panelStyle,
    onMaskClick
  };
};
export default __sfc_main;
</script>

<style scoped>
.panel-modal-mask {
  position: fixed;
  inset: 0;
  display: flex;
  justify-content: center;
}

.panel-modal-mask.page-H5 {
  align-items: flex-end;
  background: rgba(0, 0, 0, 0.5);
}

.panel-modal-mask.page-PC {
  align-items: center;
  background: rgba(0, 0, 0, 0.8);
}

.panel-modal-stack {
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
}

.panel-modal-stack.page-H5 {
  width: 7.5rem;
}

.panel-modal-panel {
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: visible;
  box-sizing: border-box;
  background: var(--panel-modal-background, #ffffff);
}

.panel-modal-header {
  box-sizing: border-box;
  background: transparent;
  position: relative;
  z-index: 1;
}

.panel-modal-indicator {
  background: rgba(83, 90, 102, 0.3);
}

.panel-modal-title {
  margin: 0;
  color: #262626;
  font-family: BR Hendrix, Arial, sans-serif;
  font-weight: 600;
}

.panel-modal-body,
.panel-modal-footer {
  box-sizing: border-box;
  background: transparent;
}

.panel-modal-body {
  overflow-y: auto;
}

.panel-modal-badge {
  position: absolute;
  z-index: 1;
  pointer-events: none;
}

.panel-modal-badge-image {
  display: block;
  width: 100%;
  height: auto;
}

.panel-modal-close-button {
  position: relative;
  width: 48px;
  height: 48px;
  margin-top: 24px;
  padding: 0;
  border: 1.5px solid #ffffff;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.15);
  cursor: pointer;
}

.panel-modal-close-line {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 12px;
  height: 1.5px;
  background: #ffffff;
  transform-origin: center;
}

.panel-modal-close-line.line-left {
  transform: translate(-50%, -50%) rotate(45deg);
}

.panel-modal-close-line.line-right {
  transform: translate(-50%, -50%) rotate(-45deg);
}

/* H5:设计稿 100px = 1rem */
.panel-modal-panel.page-H5 {
  width: 7.5rem;
  max-height: 100vh;
  border-radius: 0.3077rem 0.3077rem 0 0;
}

.page-H5 .panel-modal-header {
  padding: 0.26rem 0 0.2rem;
  border-radius: 0.3077rem 0.3077rem 0 0;
}

.page-H5 .panel-modal-indicator {
  width: 0.6923rem;
  height: 0.0769rem;
  margin: 0 auto;
  border-radius: 0.1923rem;
}

.page-H5 .panel-modal-title {
  font-size: 0.5rem;
  line-height: 1.1;
  letter-spacing: -0.02em;
}

.page-H5 .panel-modal-body {
  padding: 0.26rem 0.4rem;
}

.page-H5 .panel-modal-footer {
  padding: 0.2308rem 0.4rem;
}

.page-H5 .panel-modal-home-indicator {
  height: calc(0.32rem + env(safe-area-inset-bottom));
  flex: 0 0 auto;
}

.page-H5 .panel-modal-body.content-unpadded,
.page-H5 .panel-modal-footer.content-unpadded {
  padding: 0;
}

.page-H5 .panel-modal-badge {
  top: 0;
  right: 0;
  width: 2.1rem;
}

/* PC:227:11636 视觉稿 modal,width 760,padding 32/30,radius 20 */
.panel-modal-panel.page-PC {
  width: var(--panel-modal-width, 760px);
  max-height: calc(100vh - 120px);
  padding: 32px 30px;
  border-radius: 20px;
}

.panel-modal-panel.page-PC.panel-unpadded {
  padding: 0;
}

.page-PC .panel-modal-panel > * + * {
  margin-top: 20px;
}

.page-PC .panel-modal-title {
  font-size: 28px;
  line-height: 1.2;
  letter-spacing: -0.02em;
}

.page-PC .panel-modal-badge {
  top: 0;
  right: 0;
  width: 130.55px;
}
</style>
